import { useEffect, useMemo, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { X, Trophy, FileText, Target, Plus } from 'lucide-react';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { Drawer, DrawerContent } from '../../components/ui/drawer';
import { Button } from '../../components/ui/button';
import { Tab, TabsContainer } from '../../utils/SubjectTabs';
import AllQs from '../../../public/Icons/AllSubjects.svg';
import StatCell from '../../components/ui/StatCell';
import { PerformanceModalSkeleton } from './Skeletons/PerformanceModalSkeleton';

function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, [matches, query]);
  return matches;
}

const CustomCloseButton = ({ onClick }) => (
  <Button
    variant="outline"
    size="icon"
    onClick={onClick}
    className="absolute left-1/2 -translate-x-1/2 -top-20 h-10 w-10 rounded-full bg-[var(--q3-surface-default)] hover:bg-[var(--q3-surface-default)]/90  z-50 flex items-center justify-center"
    aria-label="Close"
  >
    <X size="24" className="  text-[var(--q3-neutral-default)]" />
  </Button>
);

export function PerformanceModal({
  isOpen,
  onClose,
  selectedStudent,
  currentUser,
  isSingleSubject,
  testDetails,
  isStickyFooterCompare,
  topperStudent,
  totalResults,
  allSubjects,
}) {
  const { packId, testId } = useParams();

  const [averageStats, setAverageStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const isDesktop = useMediaQuery('(min-width: 800px)');

  // Add ref to track the tabs container scroll position
  const tabsContainerRef = useRef(null);
  const scrollPositionRef = useRef(0);

  const modalSubjects = useMemo(() => {
    const initialSubjects = [
      {
        _id: 'overall',
        title: 'Overall',
        icon: AllQs,
        color: 'var(--q3-blue-normal)',
      },
    ];

    if (isSingleSubject) {
      return initialSubjects;
    }

    if (!allSubjects || allSubjects.length === 0) {
      return initialSubjects;
    }

    const dynamicSubjects = allSubjects.map((sub) => {
      return {
        _id: sub.subjectId._id,
        title: sub.subjectId.title,
        icon: sub.subjectId.icon && (
          <img src={sub.subjectId.icon || '/placeholder.svg'} alt={sub.subjectId.title} className="h-5 w-5" />
        ),
        color: sub.subjectId.color || null,
      };
    });

    return [...initialSubjects, ...dynamicSubjects];
  }, [isSingleSubject, allSubjects]);

  const [selectedSubject, setSelectedSubject] = useState(modalSubjects[0]);

  // Save scroll position before state change
  const saveScrollPosition = () => {
    if (tabsContainerRef.current) {
      scrollPositionRef.current = tabsContainerRef.current.scrollLeft;
    }
  };

  // Restore scroll position after state change
  useEffect(() => {
    if (tabsContainerRef.current && scrollPositionRef.current !== undefined) {
      tabsContainerRef.current.scrollLeft = scrollPositionRef.current;
    }
  });

  useEffect(() => {
    if (modalSubjects.length > 0 && !modalSubjects.some((sub) => sub._id === selectedSubject._id)) {
      setSelectedSubject(modalSubjects[0]);
    }
  }, [modalSubjects, selectedSubject]);

  useEffect(() => {
    if (!isOpen && modalSubjects.length > 0) {
      setSelectedSubject(modalSubjects[0]);
      // Reset scroll position when modal closes
      scrollPositionRef.current = 0;
    }
  }, [isOpen, modalSubjects]);

  useEffect(() => {
    let isMounted = true;
    const fetchAndSetAverageStats = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`/api/test/leaderboard/average/${testId}?pack=${packId}`);
        if (isMounted) {
          setAverageStats(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching average stats:', error);
        if (isMounted) {
          setAverageStats(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (isOpen && isStickyFooterCompare && !averageStats) {
      fetchAndSetAverageStats();
    } else if (!isOpen) {
      setAverageStats(null);
    }

    return () => {
      isMounted = false;
    };
  }, [isOpen, testId, packId, isStickyFooterCompare, averageStats]);

  useEffect(() => {
    if (!isOpen || !currentUser || !testDetails || !topperStudent) {
      setComparisonData(null);
      return;
    }

    if (!isStickyFooterCompare && !selectedStudent) {
      setComparisonData(null);
      return;
    }

    if (isStickyFooterCompare && (loading || !averageStats)) {
      setComparisonData(null);
      return;
    }

    const getStudentDataForSubject = (student, subjectTitle) => {
      if (!student) return { rank: '--', totalMarkScored: '--', accuracy: '--', marksGained: '--' };

      if (isSingleSubject || subjectTitle === 'overall') {
        return {
          rank: student.rank,
          totalMarkScored: student.totalMarkScored,
          accuracy: student.accuracy,
          marksGained: student.marksGained,
        };
      } else {
        const subject = student.subjects?.find((s) => s.subjectId.title.toLowerCase() === subjectTitle);
        return {
          rank: '--',
          totalMarkScored: subject?.totalMarkScored ?? '--',
          accuracy: subject?.accuracy ?? '--',
          marksGained: '--',
        };
      }
    };

    const getAverageDataForSubject = (stats, subjectTitle) => {
      if (!stats) return { score: '--', accuracy: '--', marksGained: '--' };

      if (subjectTitle === 'overall') {
        return {
          score: stats.averages?.totalMarkScored ?? '--',
          accuracy: stats.averages?.accuracy ?? '--',
          marksGained: stats.averages?.marksGained ?? '--',
        };
      }

      const subjectAverage = stats.subjectAverages?.[subjectTitle.charAt(0).toUpperCase() + subjectTitle.slice(1)];
      return subjectAverage
        ? {
            score: subjectAverage.totalMarkScored ?? '--',
            accuracy: subjectAverage.accuracy ?? '--',
            marksGained: '--',
          }
        : { score: '--', accuracy: '--', marksGained: '--' };
    };

    const subjectTitle = selectedSubject.title.toLowerCase();
    const currentUserData = getStudentDataForSubject(currentUser, subjectTitle);
    const topperStudentData = getStudentDataForSubject(topperStudent, subjectTitle);

    let compareTargetData = {
      rank: '--',
      score: '--',
      accuracy: '--',
      positiveScore: '--',
    };

    if (isStickyFooterCompare) {
      if (averageStats) {
        const avgData = getAverageDataForSubject(averageStats, subjectTitle);
        compareTargetData = {
          rank: '--',
          score: avgData.score,
          accuracy: avgData.accuracy,
          positiveScore: avgData.marksGained,
        };
      }
    } else {
      const selectedStudentCompareData = getStudentDataForSubject(selectedStudent, subjectTitle);
      compareTargetData = {
        rank: selectedStudentCompareData.rank,
        score: selectedStudentCompareData.totalMarkScored,
        accuracy: selectedStudentCompareData.accuracy,
        positiveScore: selectedStudentCompareData.marksGained,
      };
    }

    const formatAccuracy = (acc) => (!isNaN(Number.parseFloat(acc)) ? Number.parseFloat(acc).toFixed(2) : '--');

    const getSubjectMaxMarks = () => {
      if (isSingleSubject || subjectTitle === 'overall') return testDetails.totalMarks;

      const subjectDetail = testDetails.subjects?.find((s) => s.subjectId.title.toLowerCase() === subjectTitle);
      return subjectDetail?.totalMarks || 100;
    };

    setComparisonData({
      rank: {
        user: currentUserData.rank ?? '--',
        compareTarget: compareTargetData.rank,
        topper: topperStudentData.rank ?? '--',
      },
      score: {
        user: currentUserData.totalMarkScored ?? '--',
        compareTarget: compareTargetData.score,
        topper: topperStudentData.totalMarkScored ?? '--',
      },
      accuracy: {
        user: formatAccuracy(currentUserData.accuracy),
        compareTarget: formatAccuracy(compareTargetData.accuracy),
        topper: formatAccuracy(topperStudentData.accuracy),
      },
      positiveScore: {
        user: currentUserData.marksGained ?? '--',
        compareTarget: compareTargetData.positiveScore,
        topper: topperStudentData.marksGained ?? '--',
      },
      totalMarks: getSubjectMaxMarks(),
    });
  }, [
    isOpen,
    currentUser,
    selectedStudent,
    averageStats,
    selectedSubject,
    isSingleSubject,
    testDetails,
    isStickyFooterCompare,
    topperStudent,
    allSubjects,
    loading,
  ]);

  const handleSubjectChange = (tab) => {
    // Save current scroll position before changing the subject
    saveScrollPosition();
    setSelectedSubject(tab);
  };

  const ModalContent = () => (
    <div className="border-1 rounded-2xl pb-5">
      {!isSingleSubject && (
        <div className="overflow-x-auto" ref={tabsContainerRef}>
          <TabsContainer color={selectedSubject?.color} isGlass={false} rounded={true}>
            {modalSubjects?.map((tab) => (
              <Tab
                key={tab._id}
                isActive={selectedSubject?.title === tab.title}
                color={selectedSubject?.title === tab.title ? selectedSubject?.color : 'var(--q3-neutral-light)'}
                tabColor={tab.color}
                isGlass={false}
                onClick={() => handleSubjectChange(tab)}
              >
                {tab.icon &&
                  (typeof tab.icon === 'string' ? (
                    <img src={tab.icon || '/placeholder.svg'} alt={tab.title} />
                  ) : (
                    tab.icon
                  ))}
                <p className="mb-0 text-base">{tab.title}</p>
              </Tab>
            ))}
          </TabsContainer>
        </div>
      )}

      {loading || !comparisonData ? (
        <PerformanceModalSkeleton />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px] table-fixed">
            <thead>
              <tr style={{ backgroundColor: 'var(--q3-surface-dim)' }}>
                <th
                  className={`text-left p-5 font-medium border-1 text-[var(--q3-neutral-default) text-sm ${isSingleSubject ? 'rounded-tl-2xl' : ''}`}
                >
                  Metric
                </th>
                <th className="text-center p-5 font-medium border-1 text-[var(--q3-neutral-default) text-sm">
                  {currentUser?.userId.name.split(' ')[0]} (You)
                </th>
                <th className="text-center p-5 font-medium border-1 text-[var(--q3-neutral-default) text-sm">
                  {isStickyFooterCompare
                    ? 'Students Average'
                    : `${selectedStudent?.userId.name.split(' ')[0]} (Selected Student)`}
                </th>
                <th
                  className={`text-center p-5 font-medium border-1 text-[var(--q3-neutral-default) text-sm ${isSingleSubject ? 'rounded-tr-2xl' : ''}`}
                >
                  {topperStudent?.userId.name.split(' ')[0]} (Topper)
                </th>
              </tr>
            </thead>
            <tbody>
              {selectedSubject._id === 'overall' && (
                <tr>
                  <td className="p-5 flex items-center gap-3 border-b-1">
                    <Trophy className="h-5 w-5 text-[var(--q3-orange-normal)]" /> Rank
                  </td>
                  <td className="text-center p-5 font-semibold border-1">
                    {comparisonData.rank.user} / {totalResults}
                  </td>
                  <td className="text-center p-5 font-semibold border-1">
                    {comparisonData.rank.compareTarget} / {totalResults}
                  </td>
                  <td className="text-center p-5 font-semibold border-1">
                    {comparisonData.rank.topper} / {totalResults}
                  </td>
                </tr>
              )}

              <tr>
                <td className="p-5 flex text-base items-center gap-3 border-b-1">
                  <FileText className="h-5 w-5 text-[var(--q3-blue-normal)] text-sm" /> Score
                </td>
                <td className="p-0 border-1">
                  <StatCell
                    color="var(--q3-accent-normal)"
                    top={Number.parseFloat(comparisonData.score.user) || 0}
                    bottom={comparisonData.totalMarks}
                  />
                </td>
                <td className="p-0 border-1">
                  <StatCell
                    color="var(--q3-accent-normal)"
                    top={Number.parseFloat(comparisonData.score.compareTarget) || 0}
                    bottom={comparisonData.totalMarks}
                  />
                </td>
                <td className="p-0 border-1">
                  <StatCell
                    color="var(--q3-accent-normal)"
                    top={Number.parseFloat(comparisonData.score.topper) || 0}
                    bottom={comparisonData.totalMarks}
                  />
                </td>
              </tr>
              <tr>
                <td className="p-5 flex text-base items-center gap-3 border-b-1">
                  <Target className="h-5 w-5 text-[var(--q3-fuchsia-normal)] text-sm" /> Accuracy
                </td>
                <td className="p-0 border-1">
                  <StatCell
                    color="var(--q3-fuchsia-normal)"
                    top={Number.parseFloat(comparisonData.accuracy.user) || 0}
                    bottom={100}
                    showPercentage={true}
                  />
                </td>
                <td className="p-0 border-1">
                  <StatCell
                    color="var(--q3-fuchsia-normal)"
                    top={Number.parseFloat(comparisonData.accuracy.compareTarget) || 0}
                    bottom={100}
                    showPercentage={true}
                  />
                </td>
                <td className="p-0 border-1">
                  <StatCell
                    color="var(--q3-fuchsia-normal)"
                    top={Number.parseFloat(comparisonData.accuracy.topper) || 0}
                    bottom={100}
                    showPercentage={true}
                  />
                </td>
              </tr>

              {selectedSubject._id === 'overall' && (
                <tr className="pb-5">
                  <td className="p-5 flex text-base items-center gap-3 border-b-1">
                    <Plus className="h-5 w-5 text-[var(--q3-emerald-normal)] text-sm" /> Positive Score
                  </td>

                  <td className="p-0 border-1">
                    <StatCell
                      color="var(--q3-emerald-normal)"
                      top={Number.parseFloat(comparisonData.positiveScore.user) || 0}
                      bottom={comparisonData.totalMarks}
                    />
                  </td>
                  <td className="p-0 border-1">
                    <StatCell
                      color="var(--q3-emerald-normal)"
                      top={Number.parseFloat(comparisonData.positiveScore.compareTarget) || 0}
                      bottom={comparisonData.totalMarks}
                    />
                  </td>
                  <td className="p-0 rounded-br-2xl">
                    <StatCell
                      color="var(--q3-emerald-normal)"
                      top={Number.parseFloat(comparisonData.positiveScore.topper) || 0}
                      bottom={comparisonData.totalMarks}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent showCloseButton={false} className="p-6 w-max min-w-[1000px] backdrop-blur-sm">
          <div className="relative h-full max-h-[80vh]">
            <CustomCloseButton onClick={onClose} />
            <div className="h-full overflow-y-auto">
              <h2 className="text-xl font-semibold text-center mb-6" style={{ color: 'var(--q3-neutral-default)' }}>
                Performance Comparison
              </h2>
              <ModalContent />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent showCloseButton={false} className="max-h-[90vh] py-0 px-4 backdrop-blur-sm">
        <div className="relative h-full">
          <CustomCloseButton onClick={onClose} />
          <div className="h-full overflow-y-auto pt-8">
            <h2 className="text-xl font-semibold text-center mb-6" style={{ color: 'var(--q3-neutral-default)' }}>
              Performance Comparison
            </h2>
            <ModalContent />
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
